/**
 * Injection site rotation.
 *
 * Pure scheduling: given the sites the user configured and what they have
 * already used, work out which comes next and how long each has rested. This
 * makes no claim about how often a site *should* be reused -- it reports the
 * interval and lets the user decide.
 */
import { daysBetween, type Day } from '../lib/time';

/** The default set offered when a protocol does not define its own. */
export const DEFAULT_SITES = [
  'abdomen-L', 'abdomen-R',
  'thigh-L', 'thigh-R',
  'glute-L', 'glute-R',
  'arm-L', 'arm-R',
];

export interface SiteUse {
  site: string;
  day: Day;
}

export interface SiteRest {
  site: string;
  /** Days since last use, or null if never used. */
  restDays: number | null;
  lastUsed: Day | null;
}

/**
 * Rest status for every configured site, longest-rested first.
 * Never-used sites sort ahead of all used ones.
 */
export function siteRest(sites: string[], history: SiteUse[], today: Day): SiteRest[] {
  const last = new Map<string, Day>();
  for (const use of history) {
    const prev = last.get(use.site);
    if (!prev || use.day > prev) last.set(use.site, use.day);
  }
  return sites
    .map((site) => {
      const lastUsed = last.get(site) ?? null;
      return { site, lastUsed, restDays: lastUsed ? daysBetween(lastUsed, today) : null };
    })
    .sort((a, b) => {
      if (a.restDays === null && b.restDays === null) return 0;
      if (a.restDays === null) return -1;
      if (b.restDays === null) return 1;
      return b.restDays - a.restDays;
    });
}

/**
 * The site to use next: the one rested longest, preferring any never used.
 * Returns null when no sites are configured.
 */
export function nextSite(sites: string[], history: SiteUse[], today: Day): string | null {
  if (!sites.length) return null;
  return siteRest(sites, history, today)[0]?.site ?? null;
}

/**
 * Strict cycle order instead of longest-rested: follow the configured list,
 * continuing from whichever site was used most recently.
 */
export function nextSiteInCycle(sites: string[], history: SiteUse[]): string | null {
  if (!sites.length) return null;
  const recent = [...history].sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))[0];
  if (!recent) return sites[0] ?? null;
  const idx = sites.indexOf(recent.site);
  if (idx < 0) return sites[0] ?? null;
  return sites[(idx + 1) % sites.length] ?? null;
}
